import React from "react";
import { useTranslation } from "react-i18next";
import { useFileDropHandlers } from "src/ui/utils/useFileDropHandlers";
import { makeStyles } from "tss-react/mui";

import ArrowFileUp from "@dashboard/files/components/ArrowFileUp";
import { alpha, Typography, useTheme } from "@mui/material";
import { CustomAlert, CustomAlertSeverity } from "@shared/CustomAlert/CustomAlert";
import { showErrorToast } from "@shared/toasts/Toasts";

import { useFileUpload } from "../../hooks/useFileUpload";

const useStyles = makeStyles()((theme) => ({
    wrapper: {
        position: "relative",
    },
    overlay: {
        position: "absolute",
        inset: 0,
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1),
        border: `2px dashed ${theme.palette.emteria.action}`,
        borderRadius: 12,
        backgroundColor: alpha(theme.palette.emteria.background.paperDeep, 0.9),
        pointerEvents: "none",
    },
    label: {
        fontSize: 18,
        textAlign: "center",
        color: theme.palette.emteria.text.primary,
    },
    overlayWarning: {
        borderColor: theme.palette.emteria.warning,
    },
    overlayError: {
        borderColor: theme.palette.emteria.error,
    },
    lowSlotsAlert: {
        flexShrink: 0,
        marginBottom: theme.spacing(1),
    },
}));

type Message = {
    severity: CustomAlertSeverity;
    text: string;
    show: boolean;
    color?: string;
};

type Props = {
    children: React.ReactNode;
    groupId?: number;
    disabled?: boolean;
    disabledReason?: string;
    className?: string;
};

export const FileDropOverlay = (props: Props) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["pages", "common"]);
    const theme = useTheme();

    const { onFilesSelected, availableSlots, isLimitReached, isLowOnSlots, validationError, clearValidationError } =
        useFileUpload({
            groupId: props.groupId,
        });

    const fileDropHandlers = useFileDropHandlers<HTMLDivElement>({
        onFiles: onFilesSelected,
        disabled: props.disabled,
        onDisabledDrop: () => showErrorToast(props.disabledReason),
    });

    const isDragging = fileDropHandlers.isDragging && !props.disabled;

    const getAlertMessage = (): Message => {
        if (isLimitReached) {
            return {
                severity: "error",
                text: t("common:section.uploadField.fileCountLimitReached"),
                color: theme.palette.emteria.error,
                show: !isDragging,
            };
        } else if (isLowOnSlots) {
            return {
                severity: "warning",
                text: t("common:section.uploadField.fileCountLimitWarning", { count: availableSlots }),
                color: theme.palette.emteria.warning,
                show: !isDragging,
            };
        } else {
            return {
                severity: "info",
                text: t("pages:filesPage.dropOverlay.label"),
                color: undefined,
                show: false,
            };
        }
    };

    const getOverlayMessage = (): Message => {
        if (validationError) {
            return {
                severity: "error",
                text: validationError,
                color: theme.palette.emteria.error,
                show: true,
            };
        } else if (isLimitReached) {
            return {
                severity: "error",
                text: t("common:section.uploadField.fileCountLimitReached"),
                color: theme.palette.emteria.error,
                show: isDragging,
            };
        } else if (isLowOnSlots) {
            return {
                severity: "warning",
                text: t("common:section.uploadField.fileCountLimitWarning", { count: availableSlots }),
                color: theme.palette.emteria.warning,
                show: isDragging,
            };
        } else {
            return {
                severity: "info",
                text: t("pages:filesPage.dropOverlay.label"),
                color: undefined,
                show: isDragging,
            };
        }
    };

    const alertMessage = getAlertMessage();
    const overlayMessage = getOverlayMessage();

    return (
        <React.Fragment>
            {alertMessage.show ? (
                <CustomAlert severity={alertMessage.severity} className={classes.lowSlotsAlert}>
                    {alertMessage.text}
                </CustomAlert>
            ) : null}

            <div
                className={cx(classes.wrapper, props.className)}
                data-testid="file-drop-overlay"
                onClick={clearValidationError}
                {...fileDropHandlers.dragHandlers}
            >
                {props.children}

                {overlayMessage.show ? (
                    <div
                        className={cx(
                            classes.overlay,
                            overlayMessage.severity === "warning" && classes.overlayWarning,
                            overlayMessage.severity === "error" && classes.overlayError,
                        )}
                    >
                        <ArrowFileUp color={overlayMessage.color} />
                        <Typography className={classes.label}>{overlayMessage.text}</Typography>
                    </div>
                ) : null}
            </div>
        </React.Fragment>
    );
};
